import type { DeliveryKpiDrivers, DriverShipment, MetricSeries } from "@khazzin/api-client";
import { presetRange } from "@khazzin/ui/patterns";
import { isDone } from "./stops";

/**
 * The Dashboard's figures (ADJ-1022-8b). Today's come off the cached day, so
 * they show with no signal; this week's and this month's are the driver's own
 * Delivery KPIs (EP13-S08, delivered and failed attempts) and COD collected
 * (EP13-S01's `cod_collected`), read once for the span covering both and cut
 * here — nothing is recomputed from shipments.
 */

/** This week (from Monday) and this month, and the one read covering both. */
export function periods(today: string): { week: string; month: string; from: string; to: string } {
	const week = presetRange("thisWeek", today).from;
	const month = presetRange("thisMonth", today).from;
	return { week, month, from: week < month ? week : month, to: today };
}

export interface PeriodFigures {
	delivered: number;
	failed: number;
	/** COD collected, per currency: money is never summed across currencies. */
	cod: Record<string, number>;
}

/** A period's figures: the days on or after `since` of the read. */
export function figuresSince(
	since: string,
	kpi: Pick<DeliveryKpiDrivers, "days">,
	cod: Pick<MetricSeries, "points">,
): PeriodFigures {
	const out: PeriodFigures = { delivered: 0, failed: 0, cod: {} };
	// `?? []`: a list the server once wrote as null must not take the screen down.
	for (const d of kpi.days ?? []) {
		if (d.date >= since) {
			out.delivered += d.delivered;
			out.failed += d.failed;
		}
	}
	for (const p of cod.points ?? []) {
		if (p.date != null && p.date >= since && p.currency != null && p.amountMinor != null) {
			out.cod[p.currency] = (out.cod[p.currency] ?? 0) + p.amountMinor;
		}
	}
	return out;
}

export interface TodayFigures {
	done: number;
	total: number;
	delivered: number;
	failed: number;
	returned: number;
}

// Delivery statuses only: a pickup's (collected, failed, …) are none of
// these, so a pickup counts towards progress alone.
const DELIVERED = ["delivered", "partially_delivered"];
const FAILED = ["delivery_failed", "rescheduled"];
const RETURNED = ["returning", "returned"];

/** Today's stops by what became of them. */
export function todayFigures(stops: readonly DriverShipment[]): TodayFigures {
	const count = (statuses: string[]) => stops.filter((s) => statuses.includes(s.status)).length;
	return {
		done: stops.filter((s) => isDone(s.status)).length,
		total: stops.length,
		delivered: count(DELIVERED),
		failed: count(FAILED),
		returned: count(RETURNED),
	};
}
